import { EventInteraction, useTracking } from '@eventbrite/ads';
import { Carousel } from '@eventbrite/eds-carousel';
import {
    EventCardSaveAction,
    EventCardShareAction,
    FormattedEvent,
    getShareActionProps,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import classNames from 'classnames';
import React, { useContext, useEffect, useRef } from 'react';
import {
    LISTING_HEAP_IDS,
    RELATED_EVENTS_GRID_EXPERIMENT,
    STATSIG_EVENT_NAMES,
} from '../../constants';
import { TrackingContext } from '../../contexts';
import { useGetStatsigEnrollment } from '../hooks/useGetStatsigVariant';

import { track } from '@eventbrite/datalayer-library';
import { Typography } from '@eventbrite/marmalade';
import {
    label,
    ShareType,
    trackShareAction,
} from '../../tracking/trackShareAction';
import { AffiliateCodes, GACodes } from '../../types';
import { ListingsEventCard } from '../_shared/EventCard/ListingsEventCard';
import { getSortedEvents } from '../_shared/getSortedEvents';
import styles from './OtherEventsYouMayLike.module.scss';
import { RelatedEventsChildrenProps } from './RelatedEvents';
import { RelatedEventsProps } from './types';

const CAROUSEL_HEADING = gettext('Other events you may like');

export function getEventCardMap({
    events,
    isAuthenticated,
    shareComponentProps,
    isSalesEnded,
    isSoldOut,
    locale,
}: {
    events: FormattedEvent[];
} & Pick<
    RelatedEventsProps,
    | 'locale'
    | 'isSalesEnded'
    | 'isSoldOut'
    | 'shareComponentProps'
    | 'isAuthenticated'
>): JSX.Element[] {
    return events.map((event: FormattedEvent) => {
        const affCode = AffiliateCodes.AFF_RELATED_EVENTS_LIVE;
        const eventKey = event.promotedListingMetadata?.adId || event.id;
        const shareActionProps = getShareActionProps({
            name: event.name,
            id: event.id,
            url: event.url,
            affCode: affCode,
            utmOptions: shareComponentProps.utmOptions,
            onShareItemClick: (id: string) =>
                trackShareAction(
                    label(id as ShareType),
                    GACodes.GA_CATEGORY_EVENT_CARD,
                ),
            onClick: () => {
                trackShareAction(
                    GACodes.GA_LABEL_OPEN_MODAL,
                    GACodes.GA_CATEGORY_EVENT_CARD,
                );
                if (isSoldOut || isSalesEnded) {
                    logEvent(STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_SHARE);
                }
            },
        });

        return (
            <ListingsEventCard
                key={eventKey}
                locale={locale || 'en-US'}
                event={event}
                statsigLocationString={
                    event.isPromoted
                        ? 'Related Events - Event Carousel - Promoted'
                        : undefined
                }
                affCode={affCode}
                // TODO: This should be unified with the statsigLocationString
                onClick={() =>
                    (isSalesEnded || isSoldOut) &&
                    logEvent(STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_CLICK)
                }
                actions={
                    <>
                        <EventCardSaveAction
                            isAuthenticated={!!isAuthenticated}
                            savedByYou={event.savedByYou}
                            id={event.id}
                            eventName={event.name}
                            imageUrl={event.imageUrl}
                            actionStyle="outline"
                            onClick={(saved) =>
                                (isSalesEnded || isSoldOut) &&
                                saved &&
                                logEvent(
                                    STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_LIKE,
                                )
                            }
                        />
                        <EventCardShareAction
                            actionStyle="outline"
                            {...shareActionProps}
                        />
                    </>
                }
            />
        );
    });
}

function getEventFromId(
    props: RelatedEventsChildrenProps,
    eventIdToFind: string,
    isPromoted: boolean,
) {
    const promotedEvents = props.promotedEvents || [];
    const organicEvents = props.events || [];
    if (isPromoted) {
        const eventPosition = promotedEvents.findIndex(
            (event) => event?.id === eventIdToFind,
        );
        return {
            event: promotedEvents[eventPosition],
            absolutePosition: eventPosition + 1,
        };
    }
    const eventPosition = organicEvents.findIndex(
        (event) => event?.id === eventIdToFind,
    );
    return {
        event: organicEvents[eventPosition],
        absolutePosition: eventPosition + 1,
    };
}

const DESKTOP_SLIDE_SIZE = 3;
const MOBILE_SLIDE_SIZE = 1;

const OtherEventsYouMayLike = (props: RelatedEventsChildrenProps) => {
    const currentPage = useRef({ number: 0, size: 0 });
    const context = useContext(TrackingContext);

    useEffect(() => {
        track({
            eventName: LISTING_HEAP_IDS.RECCOMENDATIONS_VIEW,
            eventData: {
                eventBucketLabel: 'Other Events you may like',
            },
        });
    }, []);

    const { ref } = useTracking<HTMLDivElement>(({ id, event }) =>
        aggregateContext({
            id,
            event,
            context,
            page: currentPage.current,
            props,
        }),
    );

    const eventsToRender = getSortedEvents(
        props.events || [],
        props.promotedEvents || [],
    );

    const eventCardMap = getEventCardMap({
        events: eventsToRender,
        isAuthenticated: props.isAuthenticated,
        shareComponentProps: props.shareComponentProps,
        isSalesEnded: props.isSalesEnded,
        isSoldOut: props.isSoldOut,
        locale: props.locale,
    });

    const wrapEventCardMapForDesktop = () => {
        const slides = [];

        for (let i = 0; i < eventCardMap.length; i += DESKTOP_SLIDE_SIZE) {
            const slideItem = eventCardMap.filter(
                (_, index) => index >= i && index < i + DESKTOP_SLIDE_SIZE,
            );
            slides.push(slideItem);
        }
        return slides;
    };

    const eventGridExperiment =
        useGetStatsigEnrollment({
            name: RELATED_EVENTS_GRID_EXPERIMENT.experimentName,
            paramName: RELATED_EVENTS_GRID_EXPERIMENT.param,
            defaultValue: RELATED_EVENTS_GRID_EXPERIMENT.defaultValue,
        }).variant === true;

    const eventCardMapForDesktop = wrapEventCardMapForDesktop().map(
        (slide, index) => {
            return (
                <div className={styles.carouselDesktop} key={index}>
                    {slide}
                </div>
            );
        },
    );
    if (!props.events || props.events.length <= 0) {
        return null;
    }

    return (
        <section
            ref={ref}
            className={classNames(
                styles.relatedEvents,
                'g-grid g-grid--page-margin-manual',
            )}
        >
            <h2 className="eds-is-hidden-accessible">{CAROUSEL_HEADING}</h2>
            <div className="eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1 eds-l-pad-hor-5 eds-l-ln-pad-hor-0 eds-l-lg-pad-hor-0 eds-l-lw-pad-hor-0 eds-l-mar-bot-16">
                {!eventGridExperiment ? (
                    <>
                        <div
                            className="eds-show-up-mn"
                            data-testid="carousel-desktop"
                        >
                            <Carousel
                                onTransition={(toPage: number) => {
                                    currentPage.current = {
                                        number: toPage,
                                        size: DESKTOP_SLIDE_SIZE,
                                    };
                                }}
                                hasTopControls={true}
                                isNotContinuous={true}
                                alwaysShowSideNavigation={true}
                                usesNewStyle={true}
                                title={CAROUSEL_HEADING}
                                fullWidth={true}
                            >
                                {eventCardMapForDesktop}
                            </Carousel>
                        </div>
                        <div
                            className="eds-show-down-sw carousel-mobile"
                            data-testid="carousel-mobile"
                        >
                            <Carousel
                                onTransition={(toPage: number) => {
                                    currentPage.current = {
                                        number: toPage,
                                        size: MOBILE_SLIDE_SIZE,
                                    };
                                }}
                                alwaysShowSideNavigation={true}
                                hasTopControls={true}
                                isNotContinuous={true}
                                usesNewStyle={true}
                                title={CAROUSEL_HEADING}
                                fullWidth={true}
                            >
                                {eventCardMap}
                            </Carousel>
                        </div>
                    </>
                ) : (
                    <>
                        <Typography as="h2" variant="heading-sm">
                            {CAROUSEL_HEADING}
                        </Typography>
                        <div className={styles.relatedEventsGrid}>
                            {eventCardMap}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

const RELATED_EVENTS_PLACEMENT_NAME = 'related_events';
const LIVE_EVENTS_PLACEMENT_NAME = 'events_carousel';

function aggregateContext({
    id,
    context,
    event: eventData,
    page,
    props,
}: Omit<EventInteraction, 'action'> & {
    context: any;
    page: { number: number; size: number };
    props: any;
}) {
    const { event, absolutePosition } = getEventFromId(
        props,
        id,
        eventData.isPromoted,
    );
    return {
        user: {
            id: context.userId,
            locale: context.locale,
            guestId: context.guestId,
            sessionId: context.sessionId,
        },
        adId: event?.promotedListingMetadata?.adId,
        place: {
            name: RELATED_EVENTS_PLACEMENT_NAME,
            position:
                absolutePosition -
                page.number * page.size +
                (page.number > 0 ? props.promotedEvents.length : 0),
            page: page.number + 1,
            context: {
                related_event_id: context.event?.id,
                is_online: context.event?.isOnline,
                place_id: context.event?.localityPlaceId,
            },
            tabKey: '',
            subInterface: {
                name: LIVE_EVENTS_PLACEMENT_NAME,
                position: '',
            },
            placementId: event?.promotedListingMetadata?.placementId,
        },
        venueId: event?.venue?.id,
    };
}

export default OtherEventsYouMayLike;
